import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Västervik?", a: "Ansökan görs till tillståndsenheten i Västerviks kommun. Verksamhet i annan kommun i Kalmar län (t.ex. Kalmar, Oskarshamn, Vimmerby, Hultsfred) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Västervik?", a: "Västerviks kommun tar ut en ansökningsavgift och en årlig tillsynsavgift beroende på tillståndstyp och alkoholomsättning – aktuella avgifter finns på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Västervik?", a: "Räkna med 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-vastervik")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Västervik" },
      ]}
      title="Serveringstillstånd Västervik – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Västervik: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.vastervik, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Var ansöker du i Västervik?", body: "Tillståndsenheten i Västerviks kommun ansvarar för serveringstillstånd. För verksamhet i grannkommuner som Oskarshamn, Vimmerby, Hultsfred eller Valdemarsvik ansöker du hos respektive kommun." },
        { h2: "Krav från Västerviks kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) har Västervik en mycket stark sommarturism kring skärgården och Visfestivalen. Uteservering och tillfälliga tillstånd står för en stor del av kommunens ärenden juni–augusti. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt." },
        { h2: "Kunskapsprovet i Västervik", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning, nykterhet och marknadsföring enligt FoHMFS 2014:7." },
        { h2: "Handläggningstid", body: "Räkna med 8–12 veckor från komplett ansökan till beslut. För säsongsverksamhet – sök under vintern inför sommaren." },
        { h2: "Tillsyn och uppföljning", body: "Västerviks kommun bedriver aktiv tillsyn under Visfestivalen och högsäsong tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd." },
        { h2: "Lokala särdrag – skärgården och Visfestivalen", body: "Västervik präglas av Smålandskusten, skärgårdskrogar och Visfestivalen. Serveringstillstånd hanteras ofta i kombination med tillstånd för uteservering, torghandel och tillfälliga arrangemang." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-kalmar", label: "Serveringstillstånd Kalmar" },
          { to: "/serveringstillstand-linkoping", label: "Serveringstillstånd Linköping" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsbank/serveringstillstand-festival", label: "Festival – serveringstillstånd" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Västervik – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Västervik: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Västervik – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Västervik." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-vastervik" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-vastervik" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Västervik", url: "https://alkoholprovet.se/serveringstillstand-vastervik" },
      ]),
    ],
  }),
});
